import { retentionCopy, retentionScope } from './retention'
import { supabase } from './supabase'

/**
 * What applies to a household's record, when it started applying, and under
 * which wording.
 *
 * THE BASIS IS THE TERMS, NOT FOUR QUESTIONS. This screen used to ask for four
 * agreements, each with its own tick. Three of them were marked required: the
 * product does nothing without them, so the tick was a notice dressed as a
 * choice, and a question that cannot be answered no is not a question. They are
 * in the terms of use now, accepted by using the product, which is a decision
 * taken deliberately and worth writing down here because the framework asks for
 * something else.
 *
 * WHAT THAT COSTS, STATED PLAINLY. Framework section 5.3 says the user
 * "separately consents to" document processing, assessment generation, the
 * retention period and sharing, and adds that consent to assessment must not
 * automatically mean consent to third-party sharing. Section 19's build list
 * names "a consent screen" as a deliverable. Removing the screen departs from
 * both, and the fourth purpose is the one the document is emphatic about. The
 * explicit action for sharing survives in the hand-off rather than in a
 * checkbox: an adviser is named, the scope is chosen field by field, a
 * confirmation is ticked, and a disclosure row records what went and when. That
 * is the thing section 5.3 is protecting, and it is still here. The separate
 * tick is not.
 *
 * WHAT IS STILL RECORDED, AND WHY IT HAS TO BE. Four rows go down on first use,
 * one per purpose, saying the basis was the terms, which version of them, and
 * the words in force at the time. Without them the privacy policy's undertaking
 * to answer an access request with what applied and how data was used could be
 * honoured from nothing but memory, and a later change to the retention period
 * would silently restate what every existing household was subject to. The
 * table is unchanged, because it was never a record of ticks: it is a record of
 * what applied.
 *
 * Append-only, like facts and for the same reason. The database enforces it:
 * there is no update or delete policy on this table and no privilege to attempt
 * one.
 */
/**
 * Three, since there is no hand-off to an adviser any more.
 *
 * It was four. Recording a sharing term as applying would claim that something
 * applies which the product cannot do: there is no screen that shares, no
 * writer into `disclosures` and nothing that names an adviser. The database
 * still permits the fourth value and the table is still there, because a
 * schema able to record a disclosure costs nothing and a migration dropping one
 * is not free to reverse.
 */
export const PURPOSES = ['PROCESSING', 'ASSESSMENT', 'RETENTION'] as const
export type Purpose = (typeof PURPOSES)[number]

/**
 * The version of the wording in force, bumped whenever the words below change.
 *
 * Stored on every row, so a change is visible in the record rather than
 * silently retroactive. The suffix moves even when the calendar does not: the
 * retention wording changed on the same day as the first version, and two
 * different sentences stored under one version is the drift this field exists
 * to prevent.
 */
export const COPY_VERSION = '2026.10.02-1'

export interface ConsentRow {
  id: string
  purpose: Purpose
  status: 'GIVEN' | 'WITHDRAWN'
  scope: Record<string, unknown>
  shown_text: string
  copy_version: string
  recorded_at: string
}

/**
 * The wording of each term, as it stands.
 *
 * NOT SHOWN ANYWHERE, and the name says so for a reason. These sentences were
 * the bodies of four consent questions, then four notices on the screens the
 * things happened on, and now neither: what the product may do is in the terms
 * of use, and the screens describe what they are doing in their own words. The
 * board carries the engine's disclaimer, the upload panel says what becomes of
 * a file, and the hand-off lists every part that would go.
 *
 * They stay here because they are what goes into the record. A row says which
 * term applied, under which wording and from when, and `shown_text` holds the
 * sentence rather than a reference to wherever it lives now: a term is read as
 * it stood at the time, and this file changes. The column's name predates the
 * decision above and is no longer literal.
 *
 * A body and nothing else. Each of these had a title, for a heading above a
 * checkbox and then for a row on the record listing what applies; both are
 * gone, and a title nothing reads is a sentence that drifts from the one being
 * stored beside it.
 */
export const TERMS_IN_FORCE: Record<Purpose, { body: string }> = {
  PROCESSING: {
    body: 'We open each policy you upload, find the figures on the page, and show you the line each one came from. The file stays on your device until you upload it, and its contents are never used to train a general-purpose model.',
  },
  ASSESSMENT: {
    body: 'We compare what you told us with what your policies cover and show you where the two differ, with the arithmetic laid out. This is an educational screening. It is not advice, not a recommendation to buy, change or cancel anything, and not a decision about whether any policy is suitable for you.',
  },
  /** The one whose words are built, because the period is a setting. */
  RETENTION: retentionCopy(),
}

const db = () => {
  if (!supabase) throw new Error('This deployment has no database configured.')
  return supabase
}

export async function listConsents(householdId: string): Promise<ConsentRow[]> {
  const { data, error } = await db()
    .from('consents')
    .select('*')
    .eq('household_id', householdId)
    .order('recorded_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as ConsentRow[]
}

/**
 * Where each purpose stands, from the rows.
 *
 * The latest row per purpose wins, which is what append-only means when
 * something is read rather than audited. Absent is not the same as withdrawn:
 * one is a question nobody has been asked and the other is an answer.
 */
export function currentConsents(rows: ConsentRow[]): Record<Purpose, ConsentRow | null> {
  const out = Object.fromEntries(PURPOSES.map((p) => [p, null])) as Record<
    Purpose,
    ConsentRow | null
  >
  for (const row of rows) {
    if (out[row.purpose] === null) out[row.purpose] = row
  }
  return out
}

/**
 * Put down what applies to this household, once.
 *
 * On first use, and again only when the wording moves: a household subject to
 * one version of the retention period has not been told what a later one says,
 * and the row is what makes the difference answerable. Comparing the version
 * rather than merely checking for any row at all is the whole value of storing
 * it.
 *
 * Failure is swallowed deliberately, and this is the one place in the app where
 * that is right. The rows are a record of what applied, not a permission to
 * proceed: the basis is the terms, which the household accepted by being here,
 * so refusing to show somebody their own screening because an audit insert
 * failed would be withholding the product over bookkeeping. A missing row is a
 * gap in the record, and the caller logs it.
 */
export async function applyTerms(householdId: string, held: ConsentRow[]): Promise<boolean> {
  const current = currentConsents(held)
  const stale = PURPOSES.filter((p) => current[p]?.copy_version !== COPY_VERSION)
  if (stale.length === 0) return false

  for (const purpose of stale) await record({ householdId, purpose, status: 'GIVEN' })
  return true
}

export async function record(input: {
  householdId: string
  purpose: Purpose
  status: 'GIVEN' | 'WITHDRAWN'
  scope?: Record<string, unknown>
}): Promise<void> {
  const { error } = await db().from('consents').insert({
    household_id: input.householdId,
    purpose: input.purpose,
    status: input.status,
    /**
     * The retention row carries the period it was agreed under, unasked.
     *
     * The privacy policy undertakes to record the scope of a consent, and for
     * this purpose the period is the scope. It also makes a later change
     * answerable: a row saying `months: null` was an agreement to an
     * open-ended period, and the household that gave it has not agreed to what
     * a figure set afterwards says. Without it, changing the setting would
     * quietly restate what everybody had already agreed to.
     */
    scope:
      input.purpose === 'RETENTION'
        ? { ...retentionScope(), ...(input.scope ?? {}) }
        : (input.scope ?? {}),
    // The words as they were, not a reference to where they live now.
    shown_text: TERMS_IN_FORCE[input.purpose].body,
    copy_version: COPY_VERSION,
  })
  if (error) throw error
}
